import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { usePerformanceOverviewSubBranchHook } from './hooks/usePerformanceOverviewSubBranchHook';
import DatePicker from '../../components/DatePicker';
import ReportDownloadComponent from '../../components/DownloadReport/ReportDownloadComponent';
import NoData from '../../components/NoData';
import Table from '../../components/Table';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import {
  PEFORMANCE_OVERVIEW_SUB_BRANCH_FILE_NAME,
  PEFORMANCE_OVERVIEW_OPPS_TO_PLAN_FILE_NAME,
} from './constants/PerformanceOverviewConstants';
import { PERFORMANCE_OVERVIEW_SUB_BRANCH } from '../../constants/datePickerConstants';
import { performanceOverviewColumns } from './services/PerformanceOverviewSubBranchServices';
import { ErrorComponent } from '../../components/ErrorComponent/ErrorComponent';
import { getColorStyleForCell } from '../../utils/colorSchmeOverallPerformance';

export const PeformanceOverviewSubBranch = () => {
  const {
    options,
    defaultFilter,
    tableData,
    isLoading,
    isError,
    apiErrorMessages,
    isReportDownloading,
    setIsReportDownloading,
    downloadFilters,
    getErrorMessage,
    // Table 2 data
    oppsToPlantData,
    oppsToPlantColumns,
    isOppsToPlantLoading,
    isOppsToPlantError,
    isOppsToPlantDownloading,
    setIsOppsToPlantDownloading,
    oppsDownloadFilters,
  } = usePerformanceOverviewSubBranchHook();

  // Return error component if no stores are assigned
  if (options?.length === 0) {
    return <ErrorComponent />;
  }

  return (
    <div style={{ height: '88vh', overflow: 'scroll', padding: '20px', paddingBottom: '100px' }}>
      <div style={{ width: 'fitContent', marginBottom: '20px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            marginLeft: '64px',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Performance Overview Subbranches</span>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <DatePicker
              setFilter={defaultFilter}
              component={PERFORMANCE_OVERVIEW_SUB_BRANCH}
              combineCompare={false}
              // restrictDataForPreviousYears={true}
            />
          </div>
        </div>
      </div>

      {/* Table 1 - Performance Overview Table */}
      {isError ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
        >
          <NoData textValue="Error loading data" />
        </Box>
      ) : isLoading ? (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            marginLeft: '64px',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      ) : (
        <div style={{ width: '90%', marginLeft: '64px', marginBottom: '60px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" align="left">
              Performance Overview Table
            </Typography>
            <ReportDownloadComponent
              appliedFilters={downloadFilters}
              multiComponentPayload
              filename={PEFORMANCE_OVERVIEW_SUB_BRANCH_FILE_NAME}
              setIsDownloading={setIsReportDownloading}
              isDownloading={isReportDownloading}
              disabled={isLoading || isError || !tableData?.length}
            />
          </div>

          {(apiErrorMessages.planData || apiErrorMessages.comparisonData) && (
            <Alert severity="warning" sx={{ marginBottom: 2 }} translate="yes">
              {getErrorMessage()}
            </Alert>
          )}

          {tableData?.length > 0 ? (
            <div style={{ width: '100%' }}>
              <Table
                boldHeaders={true}
                boldDataColumns={['store_id']}
                stickyColumns={['store_id']}
                data={tableData}
                columns={performanceOverviewColumns}
                applySort
                applyStyleToLastRow
                getCellProps={(cellInfo) => {
                  if (
                    ['opps_perc_to_plan', 'spend_to_plan_ytd'].includes(cellInfo.column.id) &&
                    cellInfo.value !== null &&
                    cellInfo.value !== undefined &&
                    !(cellInfo.value >= 100 && cellInfo.column.id === 'spend_to_plan_ytd')
                  ) {
                    return {
                      style: {
                        ...getColorStyleForCell(cellInfo.column.id, cellInfo.value, false),
                      },
                    };
                  } else {
                    return { style: {} };
                  }
                }}
              />
            </div>
          ) : (
            <NoData />
          )}
        </div>
      )}

      {/* Table 2 - Opportunities to Plan */}
      {isOppsToPlantError ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            marginLeft: '64px',
            width: '90%',
          }}
        >
          <NoData textValue="Error loading opportunities to plan data" />
        </Box>
      ) : isOppsToPlantLoading ? (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            marginLeft: '64px',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      ) : (
        <div style={{ width: '90%', marginLeft: '64px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" align="left">
              Opportunities to Plan
            </Typography>
            <ReportDownloadComponent
              appliedFilters={oppsDownloadFilters}
              multiComponentPayload
              filename={PEFORMANCE_OVERVIEW_OPPS_TO_PLAN_FILE_NAME}
              setIsDownloading={setIsOppsToPlantDownloading}
              isDownloading={isOppsToPlantDownloading}
              disabled={isOppsToPlantLoading || isOppsToPlantError || !oppsToPlantData?.length}
            />
          </div>

          {oppsToPlantData?.length > 0 ? (
            <div style={{ width: '100%' }}>
              <Table
                boldHeaders={true}
                boldDataColumns={['branch_name']}
                stickyColumns={['branch_name', 'ytd_perc_to_plan']}
                data={oppsToPlantData}
                columns={oppsToPlantColumns}
                applySort
                applyStyleToLastRow
                getCellProps={(cellInfo) => {
                  // Apply color coding to all percentage columns except branch_name and store_id
                  if (
                    cellInfo.column.id !== 'branch_name' &&
                    cellInfo.column.id !== 'store_id' &&
                    cellInfo.value !== null &&
                    cellInfo.value !== undefined
                  ) {
                    return {
                      style: {
                        ...getColorStyleForCell('opps_perc_to_plan', cellInfo.value, false),
                      },
                    };
                  }
                  return { style: {} };
                }}
                allowBorderForSticky={false}
              />
            </div>
          ) : (
            <NoData />
          )}
        </div>
      )}
    </div>
  );
};

export default PeformanceOverviewSubBranch;
